import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeftRight } from "lucide-react";

interface LogCounterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractId: number;
  onSuccess: () => void;
}

export function LogCounterDialog({
  open,
  onOpenChange,
  contractId,
  onSuccess,
}: LogCounterDialogProps) {
  const { getToken } = useAuth();
  const { toast } = useToast();
  const [isPending, setIsPending] = useState(false);
  const [counterPartyName, setCounterPartyName] = useState("");
  const [note, setNote] = useState("");

  const handleClose = () => {
    if (!isPending) {
      onOpenChange(false);
      setCounterPartyName("");
      setNote("");
    }
  };

  const handleSubmit = async () => {
    if (!counterPartyName.trim()) {
      toast({ title: "Missing fields", description: "Please enter the counter-party's name.", variant: "destructive" });
      return;
    }
    setIsPending(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/log-counter`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          counterPartyName,
          note: note || undefined,
          changedClauses: [],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Failed to log counter-proposal");
      }
      onSuccess();
      onOpenChange(false);
      setCounterPartyName("");
      setNote("");
      toast({
        title: "Counter-proposal logged",
        description: `Response from ${counterPartyName} recorded. The contract is now in "Counter Received" state for internal review.`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not log counter-proposal.";
      toast({ title: "Error", description: message, variant: "destructive" });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Log Counter-Proposal</DialogTitle>
          <DialogDescription>
            Record that the coach or their representation has returned a response. The contract will move to "Counter Received" state for internal review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="counter-party-name">Counter-Party Name <span className="text-destructive">*</span></Label>
            <Input
              id="counter-party-name"
              placeholder="e.g. Coach Smith's Agent"
              value={counterPartyName}
              onChange={(e) => setCounterPartyName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="counter-note">Summary of Changes (optional)</Label>
            <Textarea
              id="counter-note"
              placeholder="Briefly describe what terms they are requesting to change…"
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            You can view and respond to specific clause changes in the Clauses tab after logging this counter-proposal.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            <ArrowLeftRight className="h-4 w-4 mr-2" />
            {isPending ? "Logging…" : "Log Counter-Proposal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
